import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define IDocument interface locally since we can't import it
export interface IDocument {
  id: string;
  [key: string]: any;
}

// State Interface
interface EntityNavigationState {
  currentDocumentId: string | null;
  previousDocument: IDocument | null;
  nextDocument: IDocument | null;
  documentPosition: number;
  loading: boolean;
  error: string | null;
}

const initialState: EntityNavigationState = {
  currentDocumentId: null,
  previousDocument: null,
  nextDocument: null,
  documentPosition: 0,
  loading: false,
  error: null
};

// --- PAYLOAD INTERFACES ---
export interface SetCurrentDocumentIdPayload {
  documentId: string;
}

export interface FetchAdjacentDocumentRequestPayload {
  companyId: string;
  documentId: string;
  direction: "previous" | "next";
  status: string;
}

export interface FetchAdjacentDocumentSuccessPayload {
  document: IDocument | null;
  direction: "previous" | "next";
}

// For failure action, payload can be empty if error is in action.error
export interface FetchAdjacentDocumentFailurePayload {
  // Explicitly empty if no other payload data is needed
}

export interface SetDocumentPositionPayload {
  position: number;
}

// --- ACTION TYPES (for typing reducer actions) ---
type SetCurrentDocumentIdAction = PayloadAction<SetCurrentDocumentIdPayload>;
export type FetchAdjacentDocumentRequestAction = PayloadAction<
  FetchAdjacentDocumentRequestPayload,
  string,
  {
    onFinalDocument?: {
      redirectTo?: string;
    };
    onFailure?: {
      redirectTo?: string;
    };
  }
>;
type FetchAdjacentDocumentSuccessAction =
  PayloadAction<FetchAdjacentDocumentSuccessPayload>;
type FetchAdjacentDocumentFailureAction = PayloadAction<
  FetchAdjacentDocumentFailurePayload, // Payload type
  string, // Action type string
  unknown, // Meta type
  string // Error type
>;
type SetDocumentPositionAction = PayloadAction<SetDocumentPositionPayload>;

export const entityNavigationSlice = createSlice({
  name: "entityNavigation",
  initialState,
  reducers: {
    // Set the current document ID
    setCurrentDocumentId: {
      reducer: (
        state: EntityNavigationState,
        action: SetCurrentDocumentIdAction
      ) => {
        state.currentDocumentId = action.payload.documentId;
      },
      prepare: (
        documentId: string
      ): { payload: SetCurrentDocumentIdPayload } => ({
        payload: { documentId }
      })
    },

    // Request to fetch adjacent document
    fetchAdjacentDocumentRequest: {
      reducer: (
        state: EntityNavigationState,
        _action: FetchAdjacentDocumentRequestAction
      ) => {
        state.loading = true;
        state.error = null;
      },
      prepare: (
        companyId: string,
        documentId: string,
        direction: "previous" | "next",
        status: string,
        actionsOnCompletion?: {
          onFinalDocument?: {
            redirectTo?: string;
          };
          onFailure?: {
            redirectTo?: string;
          };
        }
      ) => ({
        payload: { companyId, documentId, direction, status },
        meta: actionsOnCompletion ?? {}
      })
    },

    // Success handler for fetching adjacent document
    fetchAdjacentDocumentSuccess: {
      reducer: (
        state: EntityNavigationState,
        action: FetchAdjacentDocumentSuccessAction
      ) => {
        const { document, direction } = action.payload;
        state.loading = false;
        if (document) {
          state.currentDocumentId = document.id;
          state.documentPosition += direction === "next" ? 1 : -1;

          if (direction === "previous") {
            state.previousDocument = document;
          } else {
            // direction === "next"
            state.nextDocument = document;
          }
        }
      },
      prepare: (
        document: IDocument | null,
        direction: "previous" | "next"
      ): { payload: FetchAdjacentDocumentSuccessPayload } => ({
        payload: { document, direction }
      })
    },

    // Failure handler for fetching adjacent document
    fetchAdjacentDocumentFailure: {
      reducer: (
        state: EntityNavigationState,
        action: FetchAdjacentDocumentFailureAction
      ) => {
        state.loading = false;
        state.error = action.error; // Error is accessed from action.error
      },
      prepare: (
        errorMessage: string
      ): {
        payload: FetchAdjacentDocumentFailurePayload;
        error: string;
        meta: unknown;
      } => ({
        payload: {}, // Empty payload as error is handled separately
        error: errorMessage,
        meta: undefined
      })
    },

    // Update document position
    setDocumentPosition: {
      reducer: (
        state: EntityNavigationState,
        action: SetDocumentPositionAction
      ) => {
        state.documentPosition = action.payload.position;
      },
      prepare: (position: number): { payload: SetDocumentPositionPayload } => ({
        payload: { position }
      })
    }
  }
});

export const {
  setCurrentDocumentId,
  fetchAdjacentDocumentRequest,
  fetchAdjacentDocumentSuccess,
  fetchAdjacentDocumentFailure,
  setDocumentPosition
} = entityNavigationSlice.actions;

export default entityNavigationSlice.reducer;
